import React, { useEffect } from "react";
import Toolbar from "@mui/material/Toolbar";
import { Box, Grid, Typography, Divider } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Footer from "../Components/Footer/Footer";
import MenuCard from "../Components/MenuCards/MenuCards";
import { BanhMiItems } from "../utils/contants";
import useMetaTracking from "../hooks/useMetaTracking";

// Vegan images
import VeganPorkMeatballs from "../Assets/vegan_pork_meatballs.png";
import VeganPateMeatloaf from "../Assets/vegan_pate_meatloaf.png";
import VeganGrilledPork from "../Assets/vegan_grilled_pork.png";
import VeganSpecialCombo from "../Assets/vegan_special_combo.png";
import VeganShreddedPorkSkin from "../Assets/vegan_shredded_pork_skin.png";
import VeganGrilledChicken from "../Assets/vegan_grilled_chicken.png";
import VeganLemongrassSteak from "../Assets/vegan_lemongrass_steak.png";
import VeganFriedChicken from "../Assets/vegan_ga_chien.png";

const VeganBanhMiItems = [
  { img: VeganPorkMeatballs, title: "Vegan Pork Meatballs – $8.50", subTitle: "Xíu Mại Chay", path: "/vegan-pork-meatballs" },
  { img: VeganPateMeatloaf, title: "Vegan Pâté Meat Loaf – $8.50", subTitle: "Pâté Chả Lụa Chay", path: "/vegan-pate-meatloaf" },
  { img: VeganGrilledPork, title: "Vegan Grilled Pork – $9.00", subTitle: "Thịt Nướng Chay", path: "/vegan-grilled-pork" },
  { img: VeganSpecialCombo, title: "Vegan Special Combination – $9.50", subTitle: "Đặc Biệt Chay", path: "/vegan-special-combo" },
  { img: VeganShreddedPorkSkin, title: "Vegan Shredded Pork Skin – $8.50", subTitle: "Bì Chay", path: "/vegan-shredded-pork-skin" },
  { img: VeganGrilledChicken, title: "Vegan Grilled Chicken – $9.00", subTitle: "Gà Nướng Chay", path: "/vegan-grilled-chicken" },
  { img: VeganLemongrassSteak, title: "Vegan Lemongrass Steak – $10.00", subTitle: "Bít Tết Sả Chay", path: "/vegan-lemongrass-steak" },
  { img: VeganFriedChicken, title: "Vegan Fried Chicken – $10.50", subTitle: "Gà Chiên Chay", path: "/vegan-fried-chicken" },
];

function BanhMi() {
  const location = useLocation();
  const navigate = useNavigate();

  useMetaTracking("ViewContent", {
    content_name: "Bánh Mì Page",
    content_category: "Regular Menu",
  });

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const handleCardClick = (path) => navigate(path);

  return (
    <HelmetProvider>
      <Toolbar />

      <Helmet>
        <title>Bánh Mì | Đông Hưng Viên Bakery</title>
        <meta
          name="description"
          content="Discover our Bánh Mì offerings—traditional and vegan—made with fresh ingredients and authentic flavors. Every sandwich is crafted on a Vietnamese baguette with house-made spreads, fresh pickles, herbs, and your choice of filling."
        />
      </Helmet>

      <Box sx={{ background: "#000", py: 10, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "90%", maxWidth: "1080px" }}>

          {/* Title */}
          <Typography
            component="h1"
            sx={{
              color: "#fff",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              fontSize: { xs: "clamp(28px, 6vw, 36px)", md: "clamp(32px, 4vw, 42px)" },
              textAlign: "center",
              mb: 2,
            }}
          >
            Bánh Mì
          </Typography>

          {/* Divider */}
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.35)", mb: 3 }} />

          {/* Description */}
          <Typography
            sx={{
              color: "#fff",
              lineHeight: 1.7,
              fontSize: { xs: 16, md: 18 },
              opacity: 0.95,
              textAlign: "center",
              maxWidth: 820,
              mx: "auto",
              mb: 3,
            }}
          >
            Discover our Bánh Mì offerings—traditional and vegan—made with fresh ingredients and authentic flavors.
            Every sandwich is crafted on a Vietnamese baguette with house-made spreads, pickled daikon and carrots,
            cucumber slices, fresh cilantro, jalapeños, and soy sauce.
          </Typography>

          {/* Divider */}
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.35)", mb: 6 }} />

          {/* ---- TRADITIONAL BÁNH MÌ SECTION ---- */}
          <Grid container spacing={2} alignItems="flex-start">
            {/* Left: heading */}
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "32px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Traditional<br />Bánh Mì
              </Typography>
            </Grid>
            {/* Right: product cards */}
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                {BanhMiItems.map((item, i) => (
                  <Grid
                    key={i}
                    item
                    lg={4}
                    md={6}
                    sm={6}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <MenuCard
                      img={item.img}
                      title={item.title}
                      subTitle={item.subTitle}
                      onAddToCart={() => handleCardClick(item.link)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* ---- Separator ---- */}
          <Box sx={{ my: 8 }}>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.35)" }} />
          </Box>

          {/* ---- VEGAN BÁNH MÌ SECTION ---- */}
          <Grid container spacing={2} alignItems="flex-start">
            {/* Left: heading */}
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "32px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Vegan<br />Bánh Mì
              </Typography>
            </Grid>
            {/* Right: vegan product cards */}
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                {VeganBanhMiItems.map((item, i) => (
                  <Grid
                    key={i}
                    item
                    lg={4}
                    md={6}
                    sm={6}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <MenuCard
                      img={item.img}
                      title={item.title}
                      subTitle={item.subTitle}
                      onAddToCart={() => handleCardClick(item.path)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

        </Box>
      </Box>

      <Footer />
    </HelmetProvider>
  );
}

export default BanhMi;